import React, { useState, useEffect } from "react";
import clinicbookingService from "../../../../../services/clinicbooking.service";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Loading from "../../../../common/Loading";
import Swal from 'sweetalert2';

function AddBookingType() {
    const [apptType, setApptType] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editApptId, setEditApptId] = useState(null);
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        setValue,
        getValues,
        watch
    } = useForm();

    // Watch the timeSlot value for real-time updates
    const timeSlotValue = watch("timeSlot");

    useEffect(() => {
        getAllApptType();
    }, []);

    const getAllApptType = () => {
        setIsLoading(true);
        clinicbookingService
            .getAllApptType(clinicId)
            .then((res) => {
                setApptType(res.data);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const toggleAddForm = () => {
        setShowAddForm(!showAddForm);
        if (showAddForm) {
            reset();
        }
    };

    const handleDelete = (bookingtype) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete "${bookingtype.apptType}" appointment type? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                text: 'small',
                confirmButton: 'btn btn-danger btn-sm',
                cancelButton: 'btn btn-secondary btn-sm me-2'
            },
            buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                setIsDeleting(true);
                clinicbookingService
                    .deleteBookingType(bookingtype.clinicId, bookingtype.id)
                    .then((res) => {
                        getAllApptType();
                        Swal.fire({
                            title: 'Deleted!',
                            text: `"${bookingtype.apptType}" has been deleted successfully.`,
                            icon: 'success',
                            timer: 1500,
                            showConfirmButton: false
                        });
                    })
                    .catch((err) => {
                        console.log(err);

                        Swal.fire({
                            title: 'Error!',
                            text: 'Failed to delete appointment type. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK'
                        });
                    })
                    .finally(() => {
                        setIsDeleting(false);
                    });
            }
        });
    };

    const onSubmitEdit = (data) => {
        setIsSubmitting(true);

        // FIXED: Include clinicId in the edit data
        const editData = {
            ...data,
            id: editApptId,
            clinicId: clinicId // Add clinicId to the data
        };

        clinicbookingService
            .addUpdateBookingType(clinicId, editData)
            .then((res) => {
                getAllApptType();
                setEditApptId(null);

                Swal.fire({
                    title: 'Updated!',
                    text: 'Appointment type has been updated successfully.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            })
            .catch((err) => {
                console.log(err);

                Swal.fire({
                    title: 'Error!',
                    text: 'Failed to update appointment type. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const onSubmitNew = (data) => {
        setIsSubmitting(true);
        // FIXED: Corrected the property name from clinicID to clinicId
        const newData = { ...data, clinicId: clinicId };

        clinicbookingService
            .addUpdateBookingType(clinicId, newData)
            .then((res) => {
                reset();
                getAllApptType();
                setShowAddForm(false);

                Swal.fire({
                    title: 'Added!',
                    text: 'Appointment type has been added successfully.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            })
            .catch((err) => {
                console.log(err);

                Swal.fire({
                    title: 'Error!',
                    text: 'Failed to add appointment type. Please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    // Helper function to handle time slot increment/decrement
    const adjustTimeSlot = (amount, isEditMode = false) => {
        const fieldName = isEditMode ? "timeSlot" : "timeSlot";
        const currentValue = parseInt(getValues(fieldName) || "0");
        const newValue = Math.max(1, Math.min(480, currentValue + amount));
        setValue(fieldName, newValue, { shouldValidate: true });
    };

    return (
        <div className="container-fluid">
            <div className="row">
                {/* Header */}
                <div className="col-12 mb-4">
                    <div className="d-flex justify-content-end align-items-center mt-3">
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-info">
                                Total: {apptType.length} type{apptType.length !== 1 ? 's' : ''}
                            </span>
                            <button
                                className={`btn btn-sm small ${showAddForm ? 'btn-danger' : 'btn-outline-primary'}`}
                                onClick={toggleAddForm}
                            >
                                <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
                                {showAddForm ? 'Cancel' : 'Add New Type'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add New Appointment Type Form - Collapsible */}
                {showAddForm && (
                    <div className="col-12 mb-4">
                        <div className="card card-padding card-border shadow-none">
                            <div className="card-title mb-0">
                                <i className="bi bi-plus-circle me-2"></i>
                                Add New Appointment Type
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit(onSubmitNew)}>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label htmlFor="apptType" className="form-label fw-semibold small">
                                                    Appointment Type <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <i className="bi bi-calendar2-event"></i>
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.apptType ? "is-invalid" : ""}`}
                                                        id="apptType"
                                                        {...register("apptType", { required: "Type is required" })}
                                                        placeholder="e.g., General Checkup"
                                                    />
                                                </div>
                                                {errors.apptType && (
                                                    <div className="invalid-feedback">{errors.apptType.message}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="mb-3">
                                                <label htmlFor="timeSlot" className="form-label fw-semibold small">
                                                    Duration (minutes) <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group" style={{ maxWidth: '200px' }}>
                                                    <span className="input-group-text">
                                                        <i className="bi bi-stopwatch"></i>
                                                    </span>
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => adjustTimeSlot(-5)}
                                                    >
                                                        <i className="bi bi-dash"></i>
                                                    </button>
                                                    <input
                                                        type="number"
                                                        className={`form-control text-center ${errors.timeSlot ? "is-invalid" : ""}`}
                                                        id="timeSlot"
                                                        {...register("timeSlot", {
                                                            required: "Duration is required",
                                                            min: { value: 1, message: "Duration must be at least 1 minute" },
                                                            max: { value: 480, message: "Duration cannot exceed 8 hours" }
                                                        })}
                                                        placeholder="30"
                                                        min="1"
                                                        max="480"
                                                        style={{
                                                            MozAppearance: 'textfield',
                                                            WebkitAppearance: 'none',
                                                            appearance: 'none'
                                                        }}
                                                        onWheel={(e) => e.target.blur()} // Prevent scrolling from changing values
                                                    />
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => adjustTimeSlot(5)}
                                                    >
                                                        <i className="bi bi-plus"></i>
                                                    </button>
                                                </div>
                                                {errors.timeSlot && (
                                                    <div className="invalid-feedback d-block">{errors.timeSlot.message}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="mb-3">
                                                <label htmlFor="isActive" className="form-label fw-semibold small">
                                                    Status <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <i className="bi bi-toggle-on"></i>
                                                    </span>
                                                    <select
                                                        className={`form-select ${errors.isActive ? "is-invalid" : ""}`}
                                                        id="isActive"
                                                        {...register("isActive", { required: "Status is required" })}
                                                    >
                                                        <option value="true">Active</option>
                                                        <option value="false">Inactive</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-2 d-flex justify-content-center align-items-center">
                                            <button
                                                className="btn btn-success btn-sm mt-3"
                                                type="submit"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-plus-circle me-2"></i>
                                                        Add
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* List of Appointment Types - Table Format */}
                <div className="col-12">
                    <div className="card shadow-none">
                        <div className="card-body">
                            {isLoading ? (
                                <Loading />
                            ) : apptType.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped table-hover align-middle">
                                        <thead>
                                        <tr>
                                            <th width="40%">
                                                <i className="bi bi-calendar2-event me-2"></i>
                                                Appointment Type
                                            </th>
                                            <th width="20%">
                                                <i className="bi bi-stopwatch me-2"></i>
                                                Duration
                                            </th>
                                            <th width="20%">
                                                <i className="bi bi-toggle-on me-2"></i>
                                                Status
                                            </th>
                                            <th width="20%" className="text-center">
                                                <i className="bi bi-gear me-2"></i>
                                                Actions
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {apptType.map((appt) => (
                                            <tr key={appt.id} className={editApptId === appt.id ? "table-active" : ""}>
                                                {editApptId === appt.id ? (
                                                    <>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className={`form-control form-control-sm ${errors.apptType ? "is-invalid" : ""}`}
                                                                {...register("apptType", { required: "Type is required" })}
                                                                placeholder="e.g., General Checkup"
                                                            />
                                                            {errors.apptType && (
                                                                <div className="invalid-feedback">{errors.apptType.message}</div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="input-group input-group-sm" style={{ maxWidth: '150px' }}>
                                                                <span className="input-group-text">
                                                                    <i className="bi bi-stopwatch"></i>
                                                                </span>
                                                                <button
                                                                    className="btn btn-outline-secondary"
                                                                    type="button"
                                                                    onClick={() => adjustTimeSlot(-5, true)}
                                                                >
                                                                    <i className="bi bi-dash"></i>
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control text-center ${errors.timeSlot ? "is-invalid" : ""}`}
                                                                    {...register("timeSlot", {
                                                                        required: "Duration is required",
                                                                        min: { value: 1, message: "Duration must be at least 1 minute" },
                                                                        max: { value: 480, message: "Duration cannot exceed 8 hours" }
                                                                    })}
                                                                    placeholder="30"
                                                                    min="1"
                                                                    max="480"
                                                                    style={{
                                                                        MozAppearance: 'textfield',
                                                                        WebkitAppearance: 'none',
                                                                        appearance: 'none'
                                                                    }}
                                                                    onWheel={(e) => e.target.blur()}
                                                                />
                                                                <button
                                                                    className="btn btn-outline-secondary"
                                                                    type="button"
                                                                    onClick={() => adjustTimeSlot(5, true)}
                                                                >
                                                                    <i className="bi bi-plus"></i>
                                                                </button>
                                                            </div>
                                                            {errors.timeSlot && (
                                                                <div className="invalid-feedback d-block">{errors.timeSlot.message}</div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <select
                                                                className={`form-select form-select-sm ${errors.isActive ? "is-invalid" : ""}`}
                                                                {...register("isActive", { required: "Status is required" })}
                                                            >
                                                                <option value="true">Active</option>
                                                                <option value="false">Inactive</option>
                                                            </select>
                                                        </td>
                                                        <td className="text-end">
                                                            <div className="d-flex justify-content-end gap-2">
                                                                <button
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={handleSubmit(onSubmitEdit)}
                                                                    disabled={isSubmitting}
                                                                    title="Save changes"
                                                                >
                                                                    {isSubmitting ? (
                                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                    ) : (
                                                                        <i className="bi bi-check-lg"></i>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary btn-sm"
                                                                    onClick={() => setEditApptId(null)}
                                                                    title="Cancel edit"
                                                                >
                                                                    <i className="bi bi-x-lg"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="bi bi-clock-history text-primary me-2"></i>
                                                                <span className="fw-medium">{appt.apptType}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">
                                                                    <i className="bi bi-stopwatch me-1"></i>
                                                                    {appt.timeSlot} minutes
                                                                </span>
                                                        </td>
                                                        <td>
                                                                <span className={`badge ${appt.isActive === true ? "bg-success" : "bg-danger"}`}>
                                                                    <i className={`bi ${appt.isActive === true ? "bi-check-circle" : "bi-x-circle"} me-1`}></i>
                                                                    {appt.isActive === true ? "Active" : "Inactive"}
                                                                </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex justify-content-center gap-2">
                                                                <button
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => {
                                                                        setEditApptId(appt.id);
                                                                        setValue("apptType", appt.apptType);
                                                                        setValue("timeSlot", appt.timeSlot);
                                                                        setValue("isActive", appt.isActive.toString());
                                                                    }}
                                                                    title="Edit appointment type"
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </button>
                                                                <button
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleDelete(appt)}
                                                                    disabled={isDeleting}
                                                                    title="Delete appointment type"
                                                                >
                                                                    {isDeleting ? (
                                                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                    ) : (
                                                                        <i className="bi bi-trash"></i>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar2-x fs-1 text-muted opacity-50"></i>
                                    <h5 className="mt-3 text-muted">No Appointment Types</h5>
                                    <p className="text-muted">Get started by adding your first appointment type</p>
                                    <button
                                        className="btn btn-success mt-2"
                                        onClick={toggleAddForm}
                                    >
                                        <i className="bi bi-plus-lg me-1"></i>
                                        Add First Type
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddBookingType;